import type {Provider} from '@angular/core'
import {TranslateService} from '@ngx-translate/core'
import {ActionService} from '@service/actions/action.service'
import {AuthGuardService} from '@service/auth/auth-guard.service'
import {AuthService} from '@service/auth/auth.service'
import {DashboardService} from '@service/cloud/dashboard.service'
import {KpiService} from '@service/cloud/kpi.service'
import {ConfigurationService} from '@service/configuration/configuration.service'
import {CustomLiteralsService} from '@service/custom-literals/custom-literals.service'
import {DataService} from '@service/data.service'
import {DistributorService} from '@service/distributors/distributor.service'
import {DynamicScriptLoaderService} from '@service/dynamicScriptLoader.service'
import {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {ExtensionsService} from '@service/extensions-points/extensions.service'
import {FeatureFlagsService} from '@service/feature-flags/feature-flags.service'
import {InfoService} from '@service/info/info.service'
import {InitService} from '@service/init/init.service'
import {LoggingService} from '@service/logging.service'
import {OfferGiftOnlineService} from '@service/offer-gift/offer-gift-online.service'
import {CatalogOnlineService} from '@service/orders/catalog/catalog-online.service'
import {NewOrderService} from '@service/orders/new/new-order.service'
import {OrderLineService} from '@service/orders/order-lines/orderLine.service'
import {OrderService} from '@service/orders/order/order.service'
import {OrderStatusService} from '@service/orders/status/order-status.service'
import {TaxesService} from '@service/orders/taxes.service'
import {UMService} from '@service/orders/ums/um.service'
import {OutletService} from '@service/outlet/outlet.service'
import {PermissionsService} from '@service/permissions/permissions.service'
import {ReactiveFieldsDataService} from '@service/reactive-fields/reactive-fields-data.service'
import {StocksService} from '@service/stocks/stocks.service'
import {UVEApiService} from '@service/uve/api.service'
import {DASHBOARD_SERVICE_ADAPTER} from '@uve-solutions/dashboard'
import {KPI_SERVICE_ADAPTER} from '@uve-solutions/shared-kpis'
import {
  DEFAULT_PRODUCT_ACTIONS_STATE,
  DEFAULT_TASK_STATE,
  ProductsActionStateService,
  TasksStateService,
} from '@uve-solutions/tasks'
import {PROMOTIONAL_PACKS_SERVICE_ADAPTER} from '../shared/promotional-packs/services/promotional-packs-adapter'
import {PromotionalPacksService} from '../shared/promotional-packs/services/promotional-packs.service'
import {OrdersReactiveFieldsService, REACTIVE_FIELDS_SERVICE_ADAPTER} from '../shared/reactive-fields/public-api'
import {ORDER_REACTIVE_FIELDS_SERVICE_ADAPTER} from '../shared/reactive-fields/services/connectors/orders/orders-reactive-fields.adapter'
import {ActionServiceToken, providerActionsServiceFactory} from './actions.service.provider'
import {UVEApiServiceToken, providerUVEApiServiceFactory} from './api-uve.service.provider'
import {AuthServiceToken, providerAuthServiceFactory} from './auth.service.provider'
import {AuthGuardServiceToken, providerAuthGuardServiceFactory} from './authguard.service.provider'
import {CatalogOnlineServiceToken, providerCatalogOnlineServiceFactory} from './catalog.provider'
import {ConfigurationServiceToken, providerConfigurationServiceFactory} from './configuration.service.provider'
import {CustomLiteralsServiceToken, providerCustomLiteralsServiceFactory} from './custom-literals.service.provider'
import {DataServiceToken, providerDataServiceFactory} from './data-service.provider'
import {DistributorServiceToken, providerDistributorServiceFactory} from './distributor.service.provider'
import {ExtensionsServiceToken, providerExtensionsServiceFactory} from './extensions.service.provider'
import {FeatureFlagsServiceToken, providerFeatureFlagsServiceFactory} from './feature-flag-service.provider'
import {InfoServiceToken, providerInfoServiceFactory} from './info.service.provider'
import {LoggingServiceToken, providerLoggingServiceFactory} from './logging.provider'
import {NewOrderServiceToken, providerNewOrderFactory} from './new-order.service.provider'
import {OfferGiftOnlineServiceToken, providerOfferGiftOnlineFactory} from './offer-gift-online.service.provider'
import {OrderLinesServiceToken, providerOrderLinesFactory} from './order-lines.provider'
import {OrderStatusServiceToken, providerOrderStatusFactory} from './order-status.provider'
import {OrderServiceToken, providerOrderFactory} from './order.provider'
import {OutletServiceToken, providerOutletServiceFactory} from './outlet.service.provider'
import {PermissionsServiceToken, providerPermissionsServiceFactory} from './permissions.service.provider'
import {PacksServiceToken, providerPacksServiceFactory} from './promotional-packs.service.provider'
import {StocksServiceToken, providerStocksServiceFactory} from './stocks.service.provider'
import {TaxesServiceToken, providerTaxesServiceFactory} from './taxes.service.provider'
import {UMServiceToken, providerUMFactory} from './um.provider'

/**
 * Lista maestra de `Provider`s registrada a nivel root en `app.config.ts`.
 *
 * @description
 * Centraliza todos los providers de servicios de dominio de la aplicación.
 * Cada servicio se registra mediante dos entradas:
 *
 * 1. Una con el `InjectionToken` correspondiente (`XxxServiceToken`) y un
 *    `useFactory` (`providerXxxServiceFactory`) que devuelve la variante
 *    embebida o standalone según detecta `EmbeddedClientService`.
 * 2. Un alias `{provide: XxxService, useExisting: XxxServiceToken}` para
 *    que los consumidores inyecten directamente la clase concreta
 *    (`XxxService`) sin conocer la existencia del token.
 *
 * Además añade un bloque final de alias (`useExisting`) que conecta los
 * servicios de este repositorio con los adaptadores de las librerías
 * internas (`@uve-solutions/*`): KPIs, dashboard, tasks, productos
 * reactivos y packs promocionales.
 *
 * @usageNotes
 *
 * ### Uso básico
 * ```typescript
 * // app.config.ts
 * import {SERVICE_PROVIDERS} from '@app/providers/providers'
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     ...SERVICE_PROVIDERS,
 *     // ...otros providers (router, HTTP, i18n, etc.)
 *   ],
 * }
 * ```
 *
 * ### Best Practices
 * - Añade aquí cualquier servicio nuevo que tenga variante embebida; crea
 *   su `*.provider.ts` siguiendo el patrón factory + token.
 * - No registres estos servicios también con `@Injectable({providedIn: 'root'})`:
 *   duplicaría instancias y romperia el fallback embebido.
 * - Mantén el orden actual (primero todos los tokens, luego los alias
 *   `useExisting`) para hacer más evidente el patrón.
 *
 * ### Bad Uses / Errores comunes
 * - No inyectes los `XxxServiceToken` desde componentes; inyecta la clase
 *   (`XxxService`) y deja que el alias resuelva.
 * - No registres providers sueltos en componentes/rutas si ya están aquí:
 *   crearían instancias distintas y la detección de embebido podría
 *   evaluarse dos veces.
 */
export const SERVICE_PROVIDERS: Provider[] = [
  InitService,
  TranslateService,

  DynamicScriptLoaderService,
  EmbeddedClientService,

  {provide: UMServiceToken, useFactory: providerUMFactory, deps: [EmbeddedClientService]},
  {provide: UMService, useExisting: UMServiceToken},

  {provide: OrderLinesServiceToken, useFactory: providerOrderLinesFactory, deps: [EmbeddedClientService]},
  {provide: OrderLineService, useExisting: OrderLinesServiceToken},

  {provide: InfoServiceToken, useFactory: providerInfoServiceFactory, deps: [EmbeddedClientService]},
  {provide: InfoService, useExisting: InfoServiceToken},

  {provide: ConfigurationServiceToken, useFactory: providerConfigurationServiceFactory, deps: [EmbeddedClientService]},
  {provide: ConfigurationService, useExisting: ConfigurationServiceToken},

  {
    provide: CustomLiteralsServiceToken,
    useFactory: providerCustomLiteralsServiceFactory,
    deps: [EmbeddedClientService],
  },
  {provide: CustomLiteralsService, useExisting: CustomLiteralsServiceToken},

  {provide: OutletServiceToken, useFactory: providerOutletServiceFactory, deps: [EmbeddedClientService]},
  {provide: OutletService, useExisting: OutletServiceToken},

  {provide: ActionServiceToken, useFactory: providerActionsServiceFactory, deps: [EmbeddedClientService]},
  {provide: ActionService, useExisting: ActionServiceToken},

  {provide: StocksServiceToken, useFactory: providerStocksServiceFactory, deps: [EmbeddedClientService]},
  {provide: StocksService, useExisting: StocksServiceToken},

  {provide: DistributorServiceToken, useFactory: providerDistributorServiceFactory, deps: [EmbeddedClientService]},
  {provide: DistributorService, useExisting: DistributorServiceToken},

  {provide: PacksServiceToken, useFactory: providerPacksServiceFactory, deps: [EmbeddedClientService]},
  {provide: PromotionalPacksService, useExisting: PacksServiceToken},

  {provide: OfferGiftOnlineServiceToken, useFactory: providerOfferGiftOnlineFactory, deps: [EmbeddedClientService]},
  {provide: OfferGiftOnlineService, useExisting: OfferGiftOnlineServiceToken},

  {provide: AuthGuardServiceToken, useFactory: providerAuthGuardServiceFactory, deps: [EmbeddedClientService]},
  {provide: AuthGuardService, useExisting: AuthGuardServiceToken},

  {provide: AuthServiceToken, useFactory: providerAuthServiceFactory, deps: [EmbeddedClientService]},
  {provide: AuthService, useExisting: AuthServiceToken},

  {provide: NewOrderServiceToken, useFactory: providerNewOrderFactory, deps: [EmbeddedClientService]},
  {provide: NewOrderService, useExisting: NewOrderServiceToken},

  {provide: OrderServiceToken, useFactory: providerOrderFactory, deps: [EmbeddedClientService]},
  {provide: OrderService, useExisting: OrderServiceToken},

  {provide: OrderStatusServiceToken, useFactory: providerOrderStatusFactory, deps: [EmbeddedClientService]},
  {provide: OrderStatusService, useExisting: OrderStatusServiceToken},

  {provide: DataServiceToken, useFactory: providerDataServiceFactory, deps: [EmbeddedClientService]},
  {provide: DataService, useExisting: DataServiceToken},

  {provide: FeatureFlagsServiceToken, useFactory: providerFeatureFlagsServiceFactory, deps: [EmbeddedClientService]},
  {provide: FeatureFlagsService, useExisting: FeatureFlagsServiceToken},

  {provide: ExtensionsServiceToken, useFactory: providerExtensionsServiceFactory, deps: [EmbeddedClientService]},
  {provide: ExtensionsService, useExisting: ExtensionsServiceToken},

  {provide: PermissionsServiceToken, useFactory: providerPermissionsServiceFactory, deps: [EmbeddedClientService]},
  {provide: PermissionsService, useExisting: PermissionsServiceToken},

  {provide: UVEApiServiceToken, useFactory: providerUVEApiServiceFactory, deps: [EmbeddedClientService]},
  {provide: UVEApiService, useExisting: UVEApiServiceToken},

  {provide: LoggingServiceToken, useFactory: providerLoggingServiceFactory, deps: [EmbeddedClientService]},
  {provide: LoggingService, useExisting: LoggingServiceToken},

  {provide: CatalogOnlineServiceToken, useFactory: providerCatalogOnlineServiceFactory, deps: [EmbeddedClientService]},
  {provide: CatalogOnlineService, useExisting: CatalogOnlineServiceToken},

  {provide: TaxesServiceToken, useFactory: providerTaxesServiceFactory, deps: [EmbeddedClientService]},
  {provide: TaxesService, useExisting: TaxesServiceToken},

  {provide: KPI_SERVICE_ADAPTER, useExisting: KpiService},
  {provide: DASHBOARD_SERVICE_ADAPTER, useExisting: DashboardService},
  {provide: DEFAULT_TASK_STATE, useExisting: TasksStateService},
  {provide: DEFAULT_PRODUCT_ACTIONS_STATE, useExisting: ProductsActionStateService},
  {provide: REACTIVE_FIELDS_SERVICE_ADAPTER, useExisting: ReactiveFieldsDataService},
  {provide: ORDER_REACTIVE_FIELDS_SERVICE_ADAPTER, useExisting: OrdersReactiveFieldsService},
  {provide: PROMOTIONAL_PACKS_SERVICE_ADAPTER, useExisting: PromotionalPacksService},
]
